
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AdminBill } from "./types";
import { formatDate, formatPrice, getStatusColor } from "./utils";
import { Mail, Printer, X } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface InvoiceDialogProps {
  bill: AdminBill | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InvoiceDialog({ bill, open, onOpenChange }: InvoiceDialogProps) {
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  if (!bill) return null;

  const handleSendInvoice = async () => {
    try {
      setSending(true);
      
      // Get the user email from the subscription
      if (!bill.subscription_id && !bill.user_id) {
        throw new Error("Missing subscription or user information");
      }
      
      let userEmail = '';
      
      if (bill.subscription_id) {
        const { data: subscription, error: subError } = await supabase
          .from('subscriptions')
          .select('user_id')
          .eq('id', bill.subscription_id)
          .single();
          
        if (subError) throw subError;
        
        // Get user email
        const { data: user, error: userError } = await supabase
          .from('school_users')
          .select('email')
          .eq('id', subscription.user_id || bill.user_id)
          .single();
          
        if (userError) throw userError;
        userEmail = user.email;
      } else if (bill.user_id) {
        // Direct lookup by user_id if subscription_id is missing
        const { data: user, error: userError } = await supabase
          .from('school_users')
          .select('email')
          .eq('id', bill.user_id)
          .single();
          
        if (userError) throw userError;
        userEmail = user.email;
      }

      // Call the send-invoice-email edge function
      const { data, error } = await supabase.functions.invoke('send-invoice-email', {
        body: { 
          billId: bill.id,
          recipient: userEmail
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Invoice sent successfully",
      });
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send invoice. Please try again.",
      });
    } finally {
      setSending(false);
    }
  };

  // Calculate discount percentage for display
  let discountPercentage = null;
  if (bill.discount > 0 && bill.discount_type !== 'percentage') {
    const originalPrice = bill.plan_price || (bill.plan?.price !== undefined ? bill.plan.price : 0);
    if (originalPrice > 0) {
      discountPercentage = Math.round((bill.discount / originalPrice) * 100);
    }
  }

  const handlePrint = () => {
    window.print();
  };

  // Use the current date if created_at is not available
  const invoiceDate = bill.created_at ? formatDate(bill.created_at) : formatDate(new Date().toISOString());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>Invoice #{bill.transaction_number}</span>
            <Badge className={getStatusColor(bill.status)}>
              {bill.status.toUpperCase()}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="border rounded-lg p-6 print:border-none print:shadow-none" id="invoice-content">
          <div className="flex justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-primary">INVOICE</h2>
              <p className="text-gray-600">#{bill.transaction_number}</p>
            </div>
            <div className="text-right">
              <h3 className="font-semibold">Kasflow Educational Services</h3>
              <p className="text-gray-600">Jakarta, Indonesia</p>
              <p className="text-gray-600">support@kasflow.id</p>
            </div>
          </div>

          <div className="flex justify-between mb-8">
            <div>
              <h3 className="font-semibold mb-2">Bill To:</h3>
              <p>{bill.client?.nama_sekolah}</p>
            </div>
            <div className="text-right">
              <p><strong>Invoice Date:</strong> {invoiceDate}</p>
              <p><strong>Due Date:</strong> {formatDate(bill.due_date)}</p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="font-semibold mb-3">Item Details</h3>
            <table className="w-full">
              <thead className="border-b text-left">
                <tr>
                  <th className="py-2">Description</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-3">
                    Subscription to {bill.plan?.product?.name} - {bill.plan?.name}
                  </td>
                  <td className="py-3 text-right">
                    {formatPrice(bill.plan_price || bill.plan?.price || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <table className="w-64">
              <tbody>
                <tr>
                  <td className="py-1">Original Price:</td>
                  <td className="py-1 text-right">
                    {formatPrice(bill.plan_price || bill.plan?.price || 0)}
                  </td>
                </tr>
                {bill.discount > 0 && (
                  <>
                    <tr>
                      <td className="py-1">
                        Discount {bill.discount_type === 'percentage' 
                          ? `(${bill.discount_percentage || ''}%)` 
                          : discountPercentage ? `(≈${discountPercentage}%)` : ''}:
                      </td>
                      <td className="py-1 text-right text-red-500">
                        -{formatPrice(bill.discount)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1">Price After Discount:</td>
                      <td className="py-1 text-right">
                        {formatPrice((bill.plan_price || bill.plan?.price || 0) - bill.discount)}
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td className="py-1">Tax (10%):</td>
                  <td className="py-1 text-right">{formatPrice(bill.tax)}</td>
                </tr>
                <tr className="font-bold">
                  <td className="py-2 pt-3 border-t">Total:</td>
                  <td className="py-2 pt-3 border-t text-right">{formatPrice(bill.amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Payment Information</h3>
            <p className="text-sm text-gray-600">
              Please use your School ID as payment reference when making payment. 
              This invoice is automatically generated and sent after your subscription is created.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              For any questions regarding this invoice, please contact our support team at support@kasflow.id.
            </p>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Thank you for your business!</p>
            <p>© {new Date().getFullYear()} Kasflow.id - All rights reserved</p>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <div className="flex gap-2 w-full justify-between">
            <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" type="button" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button type="button" onClick={handleSendInvoice} disabled={sending}>
                <Mail className="h-4 w-4 mr-2" />
                {sending ? "Sending..." : "Send Invoice"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
